import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { selectOptionFactory } from '../../../../utils';
import Flex from '../../../generic/Flex';
import Section from '../../../shared/settings/SettingsPageSection';
import Dropdown from '../../shared/SettingsPageDropdown';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { ProjectGithubStatusCheckSettings } from '../../../../../shared/settings/project/githubStatusCheck';
import type { SelectOption } from '../../../../types';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';

import PropagateTimeoutSettingsModal from './PropagateTimeoutSettingsModal';
import TimeoutMinutesInput from './TimeoutMinutesInput';

type FailureOption = SelectOption<ProjectGithubStatusCheckSettings['projectDefaultIntegrationhookFailState']>;

const errorGitHubOption: FailureOption = selectOptionFactory('ERROR', 'Show a failure status');
const successGitHubOption: FailureOption = selectOptionFactory('SUCCESS', 'Show a success status');

const DEFAULT_GITHUB_FAILURE_OPTIONS: FailureOption[] = [errorGitHubOption, successGitHubOption];

//  Returns the SelectOption for whatever the selected failure option is.
const getSelectedFailureOption = (
  projectDefaultIntegrationhookFailState?: ProjectGithubStatusCheckSettings['projectDefaultIntegrationhookFailState'],
) => (projectDefaultIntegrationhookFailState === 'SUCCESS' ? successGitHubOption : errorGitHubOption);

/**
 * Displays the github status check section of the project general settings page. This section
 * allows users to timeout settings and failure mode for new projects and propagate the changes to existing projects.
 */
const GithubStatusCheckSection: VoidFunctionComponent = () => {
  const [isPropagateTimeoutModalOpen, setIsPropagateTimeoutModalOpen] = useState<boolean>(false);
  const endpoint = useMemo<OrganizationSettingsEndpoint>(
    () => `/api/organizations/${window.user?.organizationId ?? 0}/settings/projects/github-status-checks`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [window.user?.organizationId],
  );
  const { isDirty, onUpdateSettings, settings, isLoading, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<ProjectGithubStatusCheckSettings>({
      endpoint,
      settingsSection: 'github status check',
    });

  return (
    <>
      <Section>
        <Section.Left heading="Github Status Check">
          <Section.Paragraph>
            FOSSA will report a status check with the results of analyzing your build.
          </Section.Paragraph>
          <Section.Paragraph>
            You can configure this status check to timeout and fail open or closed if FOSSA is not able to analyze your
            build within the specified timeframe.
          </Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton isDisabled={isDirty} onClick={() => setIsPropagateTimeoutModalOpen(true)} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <Flex direction="column" align="flex-start" gap="16px">
            <Section.Heading>Timeout settings</Section.Heading>
            <TimeoutMinutesInput
              label="Timeout for automated builds"
              value={settings?.projectDefaultAutomatedIntegrationhookTimeout ?? 0}
              onChange={(projectDefaultAutomatedIntegrationhookTimeout: number) =>
                onUpdateSettings({
                  projectDefaultAutomatedIntegrationhookTimeout,
                })}
              isDisabled={isLoading.any}
            />
            <TimeoutMinutesInput
              label="Timeout for provided builds"
              value={settings?.projectDefaultProvidedIntegrationhookTimeout ?? 0}
              onChange={(projectDefaultProvidedIntegrationhookTimeout: number) =>
                onUpdateSettings({
                  projectDefaultProvidedIntegrationhookTimeout,
                })}
              isDisabled={isLoading.any}
            />
            <Dropdown
              id="settings-default-project-github-failure-mode"
              label="Failure mode"
              options={DEFAULT_GITHUB_FAILURE_OPTIONS}
              onChange={(option) =>
                option &&
                onUpdateSettings({
                  projectDefaultIntegrationhookFailState: (option?.value ??
                    null) as ProjectGithubStatusCheckSettings['projectDefaultIntegrationhookFailState'],
                })}
              value={getSelectedFailureOption(settings?.projectDefaultIntegrationhookFailState)}
              isDisabled={isLoading.any}
            />
          </Flex>
        </Section.Right>
      </Section>
      {settings && (
        <PropagateTimeoutSettingsModal
          isOpen={isPropagateTimeoutModalOpen}
          settings={settings}
          onHide={() => setIsPropagateTimeoutModalOpen(false)}
          endpoint={endpoint}
        />
      )}
    </>
  );
};

export default GithubStatusCheckSection;
